import { MouseEvent, ChangeEvent, FormEvent, useState } from "react";

import {
  useInput,
  useApisManagersRoleplayingConditionTemplatesIndex,
  useArray,
  useDropdown,
  useMultiDivisionMultiSectionDropdown,
  useProvidersCurrentEmployee,
} from "~/hooks";

import {
  OptionType,
  ApiManagersRoleplayingConditionTemplateIndexRequestType,
  AllPublicStatus,
  BusinessDivisionType,
  ChildType,
  ParentType,
  SingleValueType,
  RoleplayingConditionTemplateType,
} from "~/domains";

type ReturnType = {
  roleplayingConditionTemplates: RoleplayingConditionTemplateType[];
  titleKeyword: string;
  hasNextPage?: boolean;
  isLoading: boolean;
  allBusinessSectionsDistribution?: boolean;
  selectableDropdownPublicStatusIds: OptionType[];
  selectedDropdownPublicStatusId?: SingleValueType<OptionType>;
  selectableDropdownDivisions: BusinessDivisionType[];
  selectedDropdownDivisions: BusinessDivisionType[];
  selectableDropdownSections: ChildType[];
  selectedDropdownSections: ChildType[];
  onDivisionChange: (value: ParentType[]) => void;
  onSectionChange: (value: ChildType[]) => void;
  deleteIndexItem: (index: number) => void;
  unshiftItem: (item: RoleplayingConditionTemplateType) => void;
  findAndRemove: (
    func: (item: RoleplayingConditionTemplateType) => boolean,
  ) => void;
  onChangeDropdownPublicStatusId: (value: SingleValueType<OptionType>) => void;
  onChangeTitleKeyword: (e: ChangeEvent<HTMLInputElement>) => void;
  fetchNextPage: () => void;
  onConditionReset: (e: MouseEvent<HTMLButtonElement>) => void;
  onSearchSubmit: (e: FormEvent<HTMLFormElement>) => void;
  setAllBusinessSectionsDistribution: (value: boolean | undefined) => void;
};

export const useSearchManagersRoleplayingConditionTemplates =
  (): ReturnType => {
    const { currentEmployee } = useProvidersCurrentEmployee();
    const [
      { value: titleKeyword, onChange: onChangeTitleKeyword },
      setTitleKeyword,
    ] = useInput("");
    const [
      allBusinessSectionsDistribution,
      setAllBusinessSectionsDistribution,
    ] = useState<boolean | undefined>();
    const [
      {
        dropdownValue: selectedDropdownPublicStatusId,
        onChange: onChangeDropdownPublicStatusId,
        dropdownSelectableValue: selectableDropdownPublicStatusIds,
      },
      setDropdownPublicStatus,
    ] = useDropdown({
      selectableValue: AllPublicStatus.map((status) => ({
        name: status.name,
        id: status.id.toString(),
      })),
    });
    const {
      selectableDivisions,
      selectedDivisions,
      onDivisionChange,
      optionSelectableSections,
      optionSelectedSections,
      onSectionChange,
    } = useMultiDivisionMultiSectionDropdown({
      selectableDivisions: currentEmployee?.businessDivisions || [],
      selectableSections: currentEmployee?.businessSections || [],
    });
    const [params, setParams] =
      useState<ApiManagersRoleplayingConditionTemplateIndexRequestType>();

    const { items, setItems, deleteIndexItem, unshiftItem, findAndRemove } =
      useArray<RoleplayingConditionTemplateType>([]);

    const { fetchNextPage, hasNextPage, isFetching } =
      useApisManagersRoleplayingConditionTemplatesIndex({
        params,
        config: {
          onSuccess: (data) => {
            setItems(
              data.pages
                .map((page) => page.roleplayingConditionTemplates)
                .flat(),
            );
          },
        },
      });

    const onSearchSubmit = (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setParams({
        titleKeyword,
        businessDivisionIds: selectedDivisions.map((division) => division.id),
        businessSectionIds: optionSelectedSections.map((section) => section.id),
        publicStatusId: selectedDropdownPublicStatusId?.value,
        allBusinessSectionsDistribution,
      });
    };

    const onConditionReset = (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      setTitleKeyword("");
      setDropdownPublicStatus();
      onDivisionChange([]);
      onSectionChange([]);
      setParams({});
      setAllBusinessSectionsDistribution(undefined);
    };

    return {
      roleplayingConditionTemplates: items,
      titleKeyword,
      isLoading: isFetching,
      allBusinessSectionsDistribution,
      hasNextPage,
      selectableDropdownPublicStatusIds,
      selectedDropdownPublicStatusId,
      selectableDropdownDivisions: selectableDivisions,
      selectedDropdownDivisions: selectedDivisions,
      selectableDropdownSections: optionSelectableSections,
      selectedDropdownSections: optionSelectedSections,
      onDivisionChange,
      onSectionChange,
      unshiftItem,
      deleteIndexItem,
      findAndRemove,
      onChangeDropdownPublicStatusId,
      fetchNextPage,
      onChangeTitleKeyword,
      onConditionReset,
      onSearchSubmit,
      setAllBusinessSectionsDistribution,
    };
  };
